import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import {getAuth} from 'firebase/auth';
import { FaSearch } from 'react-icons/fa';
import { UserItem } from '../components/UserItem';

export default function Following() {
  const { userId } = useParams();
  const auth = getAuth();
  const [following, setFollowing] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const isCurrentUser = userId === auth.currentUser?.uid;

  // Fetch user data and who they're following
  useEffect(() => {
    const fetchFollowing = async () => {
      try {
        setIsLoading(true);
        
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const user = userSnap.data();
          setUserData({
            name: user.name,
            username: user.username
          });
          
          // Get following array - handle both current user and others
          const followingArray = user.following || [];
          
          if (followingArray.length === 0) {
            setFollowing([]);
            setIsLoading(false);
            return;
          }
          
          // Fetch each followed user's data
          const followingQuery = query(
            collection(db, 'users'),
            where('__name__', 'in', followingArray.slice(0, 30))
          );
          
          const followingSnap = await getDocs(followingQuery);
          const followingData = followingSnap.docs.map(doc => doc.id);
          setFollowing(followingData);
        }
      } catch (error) {
        console.error('Error fetching following:', error);
        setFollowing([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchFollowing();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [userId, searchQuery]);

  // Filter following based on search query
  const filteredFollowing = following.filter(followingId => {
    if (!searchQuery.trim()) return true;
    // In a real app, you'd want to filter by username/name
    return true; // Actual filtering would require fetching user data
  });

  return (
    <div className="bg-black min-h-screen">
      {/* Header with back button and user info */}
      <div className="sticky top-0 z-10 bg-black bg-opacity-90 border-b border-gray-800 p-4">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => window.history.back()}
            className="text-white hover:text-gray-300"
          >
            &larr; Back
          </button>
          {userData && (
            <div className="text-center">
              <h1 className="text-white font-bold">{userData.name}</h1>
              <p className="text-gray-400 text-sm">@{userData.username}</p>
            </div>
          )}
          <div className="w-8"></div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="sticky top-16 z-10 bg-black bg-opacity-90 border-b border-gray-800 p-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Search following..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 text-white pl-10 pr-4 py-2 rounded-full focus:outline-none focus:border-[#FF1493]"
          />
        </div>
      </div>

      {/* Count and List */}
      <div className="pb-20">
        <div className="px-4 py-3 border-b border-gray-800">
          <h2 className="text-white font-semibold">
            {filteredFollowing.length} Following
          </h2>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center py-4 px-4">
                <div className="w-12 h-12 rounded-full bg-gray-700 animate-pulse mr-3"></div>
                <div className="flex-1">
                  <div className="h-4 w-24 bg-gray-700 rounded animate-pulse mb-1"></div>
                  <div className="h-3 w-16 bg-gray-700 rounded animate-pulse"></div>
                </div>
                <div className="h-8 w-20 bg-gray-700 rounded-full animate-pulse"></div>
              </div>
            ))}
          </div>
        ) : filteredFollowing.length > 0 ? (
          filteredFollowing.map((followingId) => (
            <UserItem key={followingId} userId={followingId} />
          ))
        ) : (
          <div className="text-center py-10">
            <p className="text-gray-500">
              {searchQuery.trim() ? "No matching users found" : "Not following anyone yet"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}